import type { AccountKdfParams } from './account-keys';
import { ACCOUNTS_API_PATH, REQUEST_TIMEOUT_MS } from './constants';
import {
  AccountsUnsupportedError,
  EmailTakenError,
  InvalidCredentialsError,
  InvalidRecoveryCodeError,
  RegistrationClosedError,
  ServerErrorCode,
  SpaceTakenError,
  SyncHttpError,
  SyncProtocolError,
  VerificationLinkInvalidError,
} from './errors';
import { isRecord, normalizeBaseUrl, parseJson, sendRequest } from './transport';

export interface RegisterRequest {
  readonly email: string;
  readonly kdf: AccountKdfParams;
  readonly loginProof: string;
  readonly wrapped: string;
  readonly spaceId: string;
  /** The write token of the space (derived from the recovery code). */
  readonly spaceToken: string;
  readonly recoveryProof: string;
  /** Language tag of the user interface; selects the language of the verification mail. */
  readonly locale?: string;
}

export interface RegisterResult {
  readonly spaceId: string;
  /** False when the account was linked to an existing space (upgrade from the passphrase mode). */
  readonly created: boolean;
  readonly epoch: string;
}

export interface LoginResult {
  /** Bearer secret of the account endpoints; never logged. */
  readonly sessionToken: string;
  /** The wrapped recovery code (base64). */
  readonly wrapped: string;
  readonly spaceId: string;
  readonly kdf: AccountKdfParams;
}

export interface ResetRequest {
  readonly email: string;
  readonly recoveryProof: string;
  readonly kdf: AccountKdfParams;
  readonly loginProof: string;
  readonly wrapped: string;
}

export interface ChangePasswordRequest {
  /** Login proof of the current password. */
  readonly currentLoginProof: string;
  readonly kdf: AccountKdfParams;
  readonly loginProof: string;
  readonly wrapped: string;
}

export interface AccountInfo {
  readonly email: string;
  readonly spaceId: string;
  readonly createdAt: string;
  /** False while the server waits for the verification link to be opened; true for servers without verification. */
  readonly emailVerified: boolean;
  /**
   * Whether the server refuses uploads until the email is verified; `null` when the server does not say
   * (servers from before the field existed).
   */
  readonly verificationRequired: boolean | null;
}

export interface VerifyEmailResult {
  readonly verified: boolean;
  /** True when the link confirmed a new login email (change of email) rather than the first verification. */
  readonly emailChanged: boolean;
}

export interface ChangeEmailRequest {
  /** Login proof of the current password. */
  readonly loginProof: string;
  readonly newEmail: string;
  readonly locale?: string;
}

/** One live login session of the account; the token is never listed. */
export interface AccountSessionInfo {
  readonly id: string;
  readonly createdAt: string;
  readonly lastUsedAt: string;
  /** User-Agent header sent at login; may be empty. */
  readonly userAgent: string;
  /** True for the session that made the request. */
  readonly current: boolean;
}

/** The session token is unknown, expired or revoked. */
export class SessionRejectedError extends SyncHttpError {
  constructor(status: number, message: string) {
    super(status, ServerErrorCode.Unauthorized, message);
  }
}

export interface AccountTransportOptions {
  /** Server origin such as `https://sync.example.com`; empty for the page's own origin. */
  readonly baseUrl: string;
  readonly fetch?: typeof fetch;
  readonly timeoutMs?: number;
}

function parseKdf(value: unknown): AccountKdfParams {
  if (
    !isRecord(value) ||
    typeof value.salt !== 'string' ||
    typeof value.m !== 'number' ||
    typeof value.t !== 'number' ||
    typeof value.p !== 'number'
  ) {
    throw new SyncProtocolError('Malformed key derivation parameters in the server response');
  }
  return { salt: value.salt, m: value.m, t: value.t, p: value.p };
}

/**
 * HTTP client of the account endpoints (docs/architecture-account.md section 3.2). Reuses the timeout,
 * request identifier and error classification of `HttpTransport`; additionally maps the account error
 * codes to typed errors. Passwords, proofs, wrapped codes and session tokens appear only in request
 * bodies and headers, never in errors or logs.
 */
export class AccountTransport {
  private readonly base: string;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;

  constructor(options: AccountTransportOptions) {
    this.base = normalizeBaseUrl(options.baseUrl);
    this.fetchImpl = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.timeoutMs = options.timeoutMs ?? REQUEST_TIMEOUT_MS;
  }

  async prelogin(email: string): Promise<AccountKdfParams> {
    const body = await this.call('POST', '/prelogin', { email });
    return parseKdf(isRecord(body) ? body.kdf : undefined);
  }

  async register(request: RegisterRequest): Promise<RegisterResult> {
    const body = await this.call('POST', '/register', request);
    if (!isRecord(body) || typeof body.spaceId !== 'string' || typeof body.epoch !== 'string') {
      throw new SyncProtocolError('Malformed register response');
    }
    return { spaceId: body.spaceId, created: body.created !== false, epoch: body.epoch };
  }

  async login(email: string, loginProof: string): Promise<LoginResult> {
    const body = await this.call('POST', '/login', { email, loginProof });
    if (
      !isRecord(body) ||
      typeof body.sessionToken !== 'string' ||
      body.sessionToken === '' ||
      typeof body.wrapped !== 'string' ||
      typeof body.spaceId !== 'string'
    ) {
      throw new SyncProtocolError('Malformed login response');
    }
    return {
      sessionToken: body.sessionToken,
      wrapped: body.wrapped,
      spaceId: body.spaceId,
      kdf: parseKdf(body.kdf),
    };
  }

  async logout(sessionToken: string): Promise<void> {
    await this.call('POST', '/logout', undefined, sessionToken);
  }

  async reset(request: ResetRequest): Promise<void> {
    await this.call('POST', '/reset', request);
  }

  /**
   * Replace the credentials; the server ends every other session of the account. 403 (wrong current proof)
   * is `InvalidCredentialsError`; `SessionRejectedError`: bad session.
   */
  async changePassword(sessionToken: string, request: ChangePasswordRequest): Promise<void> {
    await this.call('POST', '/password', request, sessionToken);
  }

  async me(sessionToken: string): Promise<AccountInfo> {
    const body = await this.call('GET', '/me', undefined, sessionToken);
    if (
      !isRecord(body) ||
      typeof body.email !== 'string' ||
      typeof body.spaceId !== 'string' ||
      typeof body.createdAt !== 'string'
    ) {
      throw new SyncProtocolError('Malformed account response');
    }
    return {
      email: body.email,
      spaceId: body.spaceId,
      createdAt: body.createdAt,
      emailVerified: body.emailVerified !== false,
      verificationRequired: typeof body.verificationRequired === 'boolean' ? body.verificationRequired : null,
    };
  }

  /** Apply the token of a verification or email-change link; needs no session. Idempotent for the first kind. */
  async verifyEmail(token: string): Promise<VerifyEmailResult> {
    const body = await this.call('POST', '/verify-email', { token });
    if (!isRecord(body) || typeof body.verified !== 'boolean') {
      throw new SyncProtocolError('Malformed verify-email response');
    }
    return { verified: body.verified, emailChanged: body.emailChanged === true };
  }

  /** Send the verification mail again. 429 (`SyncHttpError`): the recipient's mail allowance is used up. */
  async resendVerification(sessionToken: string, locale?: string): Promise<void> {
    await this.call('POST', '/resend-verification', locale === undefined ? {} : { locale }, sessionToken);
  }

  /** Ask to change the login email; takes effect when the link sent to the new address is opened. */
  async changeEmail(sessionToken: string, request: ChangeEmailRequest): Promise<void> {
    await this.call('POST', '/email', request, sessionToken);
  }

  async listSessions(sessionToken: string): Promise<AccountSessionInfo[]> {
    const body = await this.call('GET', '/sessions', undefined, sessionToken);
    const list = isRecord(body) ? body.sessions : undefined;
    if (!Array.isArray(list)) throw new SyncProtocolError('Malformed sessions response');
    return list.map((entry: unknown): AccountSessionInfo => {
      if (
        !isRecord(entry) ||
        typeof entry.id !== 'string' ||
        typeof entry.createdAt !== 'string' ||
        typeof entry.lastUsedAt !== 'string'
      ) {
        throw new SyncProtocolError('Malformed session entry');
      }
      return {
        id: entry.id,
        createdAt: entry.createdAt,
        lastUsedAt: entry.lastUsedAt,
        userAgent: typeof entry.userAgent === 'string' ? entry.userAgent : '',
        current: entry.current === true,
      };
    });
  }

  /** End one session of the account (404 `SyncHttpError`: it does not exist or belongs to another account). */
  async revokeSession(sessionToken: string, id: string): Promise<void> {
    await this.call('DELETE', `/sessions/${encodeURIComponent(id)}`, undefined, sessionToken);
  }

  /** Delete the account, its space and every record. 403: wrong proof; `SessionRejectedError`: bad session. */
  async deleteAccount(sessionToken: string, loginProof: string): Promise<void> {
    await this.call('DELETE', '/me', { loginProof }, sessionToken);
  }

  private async call(
    method: string,
    path: string,
    payload?: unknown,
    sessionToken?: string,
  ): Promise<unknown> {
    const headers: Record<string, string> = { 'X-Request-ID': globalThis.crypto.randomUUID() };
    if (sessionToken !== undefined) headers.Authorization = `Bearer ${sessionToken}`;
    if (payload !== undefined) headers['Content-Type'] = 'application/json';
    try {
      const response = await sendRequest(
        this.fetchImpl,
        `${this.base}${ACCOUNTS_API_PATH}${path}`,
        { method, headers, ...(payload === undefined ? {} : { body: JSON.stringify(payload) }) },
        this.timeoutMs,
      );
      return response.text === '' ? null : parseJson(response.text);
    } catch (error) {
      throw error instanceof SyncHttpError ? this.typed(error, path, sessionToken !== undefined) : error;
    }
  }

  /** Translate a server error of an account endpoint; unmapped errors stay `SyncHttpError`. */
  private typed(error: SyncHttpError, path: string, withSession: boolean): Error {
    // A missing session of the revoke endpoint is an ordinary answer; every other 404 means the server has no
    // account endpoints at all.
    if (error.status === 404) return path.startsWith('/sessions/') ? error : new AccountsUnsupportedError();
    if (error.status === 400 && path === '/verify-email') return new VerificationLinkInvalidError();
    switch (error.code) {
      case ServerErrorCode.EmailTaken:
        return new EmailTakenError();
      case ServerErrorCode.SpaceTaken:
        return new SpaceTakenError();
      case ServerErrorCode.RegistrationClosed:
        return new RegistrationClosedError();
      default:
        break;
    }
    if (error.status === 401) {
      return withSession ? new SessionRejectedError(error.status, 'The account session is no longer valid') : new InvalidCredentialsError();
    }
    if (error.status === 403) {
      if (path === '/reset') return new InvalidRecoveryCodeError();
      if (path === '/me' || path === '/password' || path === '/email') return new InvalidCredentialsError();
    }
    return error;
  }
}
